import React, { useEffect, useMemo, useRef, useState } from 'react';
import { puantajApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Calendar, ChevronLeft, ChevronRight, Download, Upload, Plus, Trash2, X, Users, Clock, CalendarOff, BarChart3,
} from 'lucide-react';

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const WD_TR = ['PAZ', 'PZT', 'SAL', 'ÇAR', 'PER', 'CUM', 'CTS']; // getDay 0..6

const PuantajPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const canManage = ['super_admin', 'admin', 'supervisor', 'team_leader', 'ik'].includes(user?.role);

  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 }); // m 1..12
  const monthStr = `${ym.y}-${String(ym.m).padStart(2, '0')}`;

  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [gorevYeri, setGorevYeri] = useState('');
  const [cellEdit, setCellEdit] = useState(null); // { personnel_id, name, date }
  const [showAddCode, setShowAddCode] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInput = useRef(null);

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('month', monthStr);
      const res = await puantajApi.import(fd);
      const d = res.data?.data || {};
      toast({
        title: 'Nöbet planı yüklendi',
        description: `${d.matched_personnel} personel eşleşti · ${d.shifts_written} vardiya yazıldı${d.unmatched_names?.length ? ` · ${d.unmatched_names.length} isim eşleşmedi` : ''}`,
        variant: 'success',
      });
      if (d.unmatched_names?.length) {
        console.warn('Eşleşmeyen isimler:', d.unmatched_names);
      }
      load();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.message || 'Yüklenemedi', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await puantajApi.getMatrix(monthStr);
      setMatrix(res.data?.data || null);
    } catch {
      toast({ title: 'Hata', description: 'Puantaj alınamadı', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStr]);

  const dayList = useMemo(() => {
    if (!matrix) return [];
    return Array.from({ length: matrix.days }, (_, i) => {
      const d = i + 1;
      const date = new Date(ym.y, ym.m - 1, d);
      return { d, date: `${monthStr}-${String(d).padStart(2, '0')}`, wd: WD_TR[date.getDay()], weekend: [0, 6].includes(date.getDay()) };
    });
  }, [matrix, ym, monthStr]);

  const filteredRows = (matrix?.rows || []).filter((r) => {
    if (gorevYeri && r.gorev_yeri !== gorevYeri) return false;
    if (search && !r.name.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR'))) return false;
    return true;
  });

  const prevMonth = () => setYm(({ y, m }) => (m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 }));
  const nextMonth = () => setYm(({ y, m }) => (m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 }));

  const assignCell = async (personnel_id, work_date, shift_code_id, team_code) => {
    try {
      await puantajApi.setCell({ personnel_id, work_date, shift_code_id, team_code });
      setCellEdit(null);
      load();
    } catch {
      toast({ title: 'Hata', description: 'Kaydedilemedi', variant: 'destructive' });
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Bu kod silinsin mi?')) return;
    try { await puantajApi.deleteCode(id); load(); } catch { toast({ title: 'Hata', variant: 'destructive' }); }
  };

  const summary = matrix?.summary;
  const codes = matrix?.codes || [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-[1400px]">
      {/* Başlık + ay nav */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-900">Nöbet Planı & Puantaj</h1>
            <p className="text-xs text-slate-500">Aylık nöbet çizelgesi</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prevMonth}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-semibold text-slate-800 w-36 text-center">{MONTHS_TR[ym.m - 1]} {ym.y}</span>
          <Button variant="outline" size="sm" onClick={nextMonth}><ChevronRight className="h-4 w-4" /></Button>
          {canManage && (
            <>
              <input ref={importInput} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={(e) => { handleImport(e.target.files?.[0]); e.target.value = ''; }} />
              <Button variant="outline" size="sm" disabled={importing} onClick={() => importInput.current?.click()}>
                <Upload className="h-4 w-4 mr-1.5" /> {importing ? 'Yükleniyor...' : 'Excel Yükle'}
              </Button>
            </>
          )}
          <a href={puantajApi.exportUrl(monthStr)}>
            <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1.5" /> Excel İndir</Button>
          </a>
        </div>
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Toplam Personel', value: summary?.total_personnel ?? '—', icon: Users, color: 'text-slate-800' },
          { label: 'Toplam Puantaj', value: summary ? `${summary.total_hours}s` : '—', icon: Clock, color: 'text-emerald-600' },
          { label: 'İzin Günleri', value: summary?.leave_days ?? '—', icon: CalendarOff, color: 'text-purple-600' },
          { label: 'Ort. Puantaj/Kişi', value: summary ? `${summary.avg_hours}s` : '—', icon: BarChart3, color: 'text-blue-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">{c.label}</p>
              <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
            </div>
            <c.icon className="h-5 w-5 text-slate-300" />
          </div>
        ))}
      </div>

      {/* Kodlar */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        <span className="text-sm font-medium text-slate-500">Kodlar:</span>
        {codes.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold text-white" style={{ backgroundColor: c.color }}>
            {c.label}
            {canManage && (
              <button onClick={() => deleteCode(c.id)} className="opacity-80 hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
            )}
          </span>
        ))}
        {canManage && (
          <Button variant="outline" size="sm" className="h-7" onClick={() => setShowAddCode(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Kod Ekle
          </Button>
        )}
      </div>

      {/* Arama + Görev Yeri filtresi */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input placeholder="Personel ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 flex-1" />
        <select
          value={gorevYeri}
          onChange={(e) => setGorevYeri(e.target.value)}
          className="h-9 border border-slate-200 rounded-lg px-3 text-sm bg-white sm:w-56"
        >
          <option value="">Tüm Görev Yerleri</option>
          {(matrix?.gorev_yerleri || []).map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {/* Izgara */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-auto">
        {loading ? (
          <p className="text-center text-slate-400 py-12">Yükleniyor...</p>
        ) : (
          <table className="text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-left font-medium text-slate-600 min-w-[180px] border-b border-slate-200">
                  Ad Soyad / Görev
                </th>
                {dayList.map((d) => (
                  <th key={d.d} className={`px-1 py-2 text-center font-medium border-b border-slate-200 min-w-[34px] ${d.weekend ? 'text-red-500' : 'text-slate-500'}`}>
                    <div>{d.d}</div>
                    <div className="text-[9px]">{d.wd}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-medium text-slate-600 border-b border-slate-200">Top.</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan={dayList.length + 2} className="text-center text-slate-400 py-10">Personel bulunamadı</td></tr>
              ) : filteredRows.map((r) => (
                <tr key={r.personnel_id} className="hover:bg-slate-50/60">
                  <td className="sticky left-0 bg-white z-10 px-3 py-1.5 border-b border-slate-100">
                    <div className="font-medium text-slate-800 leading-tight">{r.name}</div>
                    <div className="text-[10px] text-slate-400 uppercase">
                      {r.gorev_yeri ? <span className="text-blue-500 font-semibold">{r.gorev_yeri}</span> : null}
                      {r.gorev_yeri && r.unvan ? ' — ' : ''}
                      {r.unvan || (!r.gorev_yeri && r.team_code ? r.team_code : '')}
                    </div>
                  </td>
                  {dayList.map((d) => {
                    const cell = r.cells[d.date];
                    return (
                      <td key={d.d} className="p-0.5 text-center border-b border-l border-slate-100">
                        <button
                          disabled={!canManage}
                          onClick={() => setCellEdit({ personnel_id: r.personnel_id, name: r.name, date: d.date, team_code: r.team_code })}
                          className={`w-full h-7 rounded text-[10px] font-bold ${cell ? 'text-white' : 'text-slate-300 hover:bg-slate-100'} ${canManage ? 'cursor-pointer' : 'cursor-default'}`}
                          style={cell ? { backgroundColor: cell.color } : {}}
                          title={cell ? `${cell.code} · ${cell.actual_hours != null ? `${cell.actual_hours}s (gerçek)` : `${cell.hours}s (plan)`}` : ''}
                        >
                          {cell ? cell.code : '+'}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 text-center font-semibold text-slate-700 border-b border-l border-slate-100">{r.total_hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Hücre düzenleme modalı */}
      {cellEdit && (
        <CellModal
          cellEdit={cellEdit}
          codes={codes}
          onClose={() => setCellEdit(null)}
          onPick={(codeId) => assignCell(cellEdit.personnel_id, cellEdit.date, codeId, cellEdit.team_code)}
        />
      )}

      {/* Kod ekleme modalı */}
      {showAddCode && (
        <AddCodeModal onClose={() => setShowAddCode(false)} onAdded={() => { setShowAddCode(false); load(); }} />
      )}
    </div>
  );
};

/* ── Hücre kod seçimi ── */
const CellModal = ({ cellEdit, codes, onClose, onPick }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div>
          <h3 className="font-bold text-slate-900 text-sm">{cellEdit.name}</h3>
          <p className="text-xs text-slate-400">{cellEdit.date}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:bg-slate-100 rounded-lg p-1"><X className="h-4 w-4" /></button>
      </div>
      <div className="p-4 grid grid-cols-2 gap-2">
        {codes.map((c) => (
          <button
            key={c.id}
            onClick={() => onPick(c.id)}
            className="rounded-lg px-3 py-2 text-xs font-semibold text-white text-left"
            style={{ backgroundColor: c.color }}
          >
            <div>{c.code}</div>
            <div className="text-[10px] opacity-90 font-normal">{c.label} · {c.hours}s</div>
          </button>
        ))}
      </div>
      <div className="px-4 pb-4">
        <Button variant="outline" className="w-full" onClick={() => onPick(null)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Temizle
        </Button>
      </div>
    </div>
  </div>
);

/* ── Kod ekleme ── */
const AddCodeModal = ({ onClose, onAdded }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ code: '', label: '', color: '#2563eb', hours: 0, type: 'mesai' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.label) { toast({ title: 'Hata', description: 'Kod ve açıklama zorunlu', variant: 'destructive' }); return; }
    setLoading(true);
    try { await puantajApi.addCode(form); toast({ title: 'Eklendi', variant: 'success' }); onAdded(); }
    catch (e) { toast({ title: 'Hata', description: e.response?.data?.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Yeni Kod</h2>
          <button onClick={onClose} className="text-slate-400 hover:bg-slate-100 rounded-lg p-1.5"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Kod</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="24" /></div>
          <div className="space-y-1.5"><Label>Saat</Label><Input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Açıklama</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="24 Saat Nöbet" /></div>
          <div className="space-y-1.5"><Label>Renk</Label><input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200" /></div>
          <div className="space-y-1.5">
            <Label>Tür</Label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white">
              <option value="mesai">Mesai</option><option value="izin">İzin</option><option value="rapor">Rapor</option><option value="evrak">Evrak</option>
            </select>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">{loading ? 'Ekleniyor...' : 'Ekle'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PuantajPage;
